/**
 * Reservations worklist for the desk: copies to take off the shelves and put
 * on the hold shelf, then the hold shelf itself.
 *
 * A reservation becomes available to the patron only when a librarian marks
 * it ready here (button or barcode scan): the pickup period starts then and
 * the patron is emailed.
 */

import { useId, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2, PackageCheck, ScanLine, Truck } from "lucide-react";
import {
  useLocations,
  useMarkReservationReady,
  useReservationPreparation,
  useShelveScannedReservation,
} from "@/api/hooks";
import type { PreparationReservation, ReadyForPickupResult } from "@/api/types";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const dateFormat = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short" });

type Tab = "toPrepare" | "ready";

export function ReservationsPage() {
  const { user } = useAuth();
  const locations = useLocations();
  const id = useId();

  const pickupLocations = useMemo(
    () =>
      (locations.data ?? []).filter(
        (l) => l.active && (l.type === "library" || l.type === "branch")
      ),
    [locations.data]
  );

  // The librarian's own library, or the library their section belongs to
  const ownLibrary = useMemo(() => {
    const candidates = [user?.location?.id, ...(user?.location?.path ?? []).map((p) => p.id)];
    return candidates.find((c) => pickupLocations.some((l) => l.id === c)) ?? "";
  }, [user, pickupLocations]);

  const [chosenLocation, setChosenLocation] = useState<string | null>(null);
  const pickupLocationId = chosenLocation ?? ownLibrary;
  const [tab, setTab] = useState<Tab>("toPrepare");

  const list = useReservationPreparation(pickupLocationId);
  const markReady = useMarkReservationReady();
  const [lastResult, setLastResult] = useState<ReadyForPickupResult | null>(null);

  const reservations = list.data?.[tab] ?? [];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Réservations</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Sortez les exemplaires du rayon, posez-les sur l&apos;étagère des réservations, puis
            mettez-les à disposition : le lecteur est alors prévenu et le délai de retrait commence.
          </p>
        </div>
        <div>
          <label
            htmlFor={`${id}-location`}
            className="mb-1.5 block text-sm text-gray-700 dark:text-gray-200"
          >
            Bibliothèque de retrait
          </label>
          <select
            id={`${id}-location`}
            value={pickupLocationId}
            onChange={(e) => {
              setChosenLocation(e.target.value);
              setLastResult(null);
            }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          >
            <option value="">Toutes les bibliothèques</option>
            {pickupLocations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
      </header>

      <ScanForm
        pickupLocationId={pickupLocationId}
        onShelved={(result) => {
          setLastResult(result);
        }}
      />

      {lastResult && <ResultNotice result={lastResult} />}

      <div
        role="tablist"
        aria-label="Réservations"
        className="flex gap-1 border-b border-gray-200 dark:border-gray-700"
      >
        <TabButton
          selected={tab === "toPrepare"}
          onSelect={() => {
            setTab("toPrepare");
          }}
          label="À préparer"
          count={list.data?.toPrepare.length}
          controls={`${id}-panel`}
        />
        <TabButton
          selected={tab === "ready"}
          onSelect={() => {
            setTab("ready");
          }}
          label="Prêtes au retrait"
          count={list.data?.ready.length}
          controls={`${id}-panel`}
        />
      </div>

      <section id={`${id}-panel`} role="tabpanel" aria-busy={list.isLoading}>
        {list.isLoading ? (
          <div className="space-y-2" aria-label="Chargement des réservations">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-16 animate-pulse rounded-xl bg-gray-100 motion-reduce:animate-none dark:bg-gray-800"
              />
            ))}
          </div>
        ) : list.error ? (
          <p
            role="alert"
            className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300"
          >
            <AlertCircle className="h-4 w-4" aria-hidden />
            Impossible de charger les réservations.
          </p>
        ) : reservations.length === 0 ? (
          <p className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
            {tab === "toPrepare"
              ? "Aucun exemplaire à préparer."
              : "L'étagère des réservations est vide."}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-gray-200 text-xs text-gray-500 uppercase dark:border-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Document
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Exemplaire
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Lecteur
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {tab === "toPrepare" ? "Réservé le" : "À retirer avant le"}
                  </th>
                  {tab === "toPrepare" && (
                    <th scope="col" className="px-4 py-3 text-right font-medium">
                      <span className="sr-only">Action</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {reservations.map((reservation) => (
                  <ReservationRow
                    key={reservation.id}
                    reservation={reservation}
                    showPickup={!pickupLocationId}
                    pending={markReady.isPending && markReady.variables === reservation.id}
                    onReady={
                      tab === "toPrepare"
                        ? () => {
                            markReady.mutate(reservation.id, { onSuccess: setLastResult });
                          }
                        : undefined
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {markReady.error && (
          <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-400">
            {markReady.error.message}
          </p>
        )}
      </section>
    </div>
  );
}

function TabButton({
  selected,
  onSelect,
  label,
  count,
  controls,
}: {
  selected: boolean;
  onSelect: () => void;
  label: string;
  count: number | undefined;
  controls: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      aria-controls={controls}
      onClick={onSelect}
      className={cn(
        "focus-visible:ring-ring -mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none",
        selected
          ? "border-primary text-gray-900 dark:text-white"
          : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      )}
    >
      {label}
      {count !== undefined && (
        <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">
          {count}
        </span>
      )}
    </button>
  );
}

function ReservationRow({
  reservation,
  showPickup,
  pending,
  onReady,
}: {
  reservation: PreparationReservation;
  showPickup: boolean;
  pending: boolean;
  onReady: (() => void) | undefined;
}) {
  const { item } = reservation;
  const inTransit = item?.status === "in_transit";

  return (
    <tr className="align-top">
      <td className="px-4 py-3">
        <Link
          to={`/catalog/${reservation.record.id}`}
          className="font-medium text-gray-900 hover:underline dark:text-white"
        >
          {reservation.record.title}
        </Link>
        {reservation.record.authors && (
          <p className="text-xs text-gray-500 dark:text-gray-400">{reservation.record.authors}</p>
        )}
        {showPickup && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Retrait : {reservation.pickupLocation.name}
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        {item ? (
          <>
            <p className="font-mono text-[13px] text-gray-900 dark:text-gray-100">
              {item.callNumber ?? "—"}
            </p>
            <p className="font-mono text-xs text-gray-500 dark:text-gray-400">{item.barcode}</p>
            <p
              className={cn(
                "mt-1 inline-flex items-center gap-1 text-xs",
                inTransit
                  ? "text-amber-700 dark:text-amber-400"
                  : "text-gray-500 dark:text-gray-400"
              )}
            >
              {inTransit && <Truck className="h-3.5 w-3.5" aria-hidden />}
              {inTransit ? `En transit vers ${item.location}` : item.location}
            </p>
          </>
        ) : (
          "—"
        )}
      </td>
      <td className="px-4 py-3">
        <Link
          to={`/circulation/patrons/${reservation.patron.id}`}
          className="text-gray-900 hover:underline dark:text-white"
        >
          {reservation.patron.name}
        </Link>
        <p className="font-mono text-xs text-gray-500 dark:text-gray-400">
          {reservation.patron.barcode}
        </p>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
        {reservation.status === "ready" && reservation.expiresAt
          ? dateFormat.format(new Date(reservation.expiresAt))
          : dateFormat.format(new Date(reservation.placedAt))}
        {reservation.status === "ready" && !reservation.patronNotified && (
          <p className="text-xs text-amber-700 dark:text-amber-400">Lecteur non prévenu</p>
        )}
      </td>
      {onReady && (
        <td className="px-4 py-3 text-right">
          <Button type="button" size="sm" disabled={pending} onClick={onReady}>
            {pending ? (
              <Loader2 className="animate-spin" aria-hidden />
            ) : (
              <PackageCheck aria-hidden />
            )}
            Mettre à disposition
          </Button>
        </td>
      )}
    </tr>
  );
}

/**
 * Scanning a copy while putting it on the hold shelf marks its reservation ready.
 */
function ScanForm({
  pickupLocationId,
  onShelved,
}: {
  pickupLocationId: string;
  onShelved: (result: ReadyForPickupResult) => void;
}) {
  const shelve = useShelveScannedReservation();
  const [barcode, setBarcode] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!barcode.trim()) return;

    shelve.mutate(
      pickupLocationId
        ? { barcode: barcode.trim(), pickupLocationId }
        : { barcode: barcode.trim() },
      {
        onSuccess: (result) => {
          onShelved(result);
          setBarcode("");
        },
        onSettled: () => {
          // Ready for the next scan
          inputRef.current?.focus();
        },
      }
    );
  };

  return (
    <form
      onSubmit={submit}
      className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900"
    >
      <div className="min-w-64 flex-1">
        <label
          htmlFor={`${id}-barcode`}
          className="mb-1.5 flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200"
        >
          <ScanLine className="h-4 w-4" aria-hidden />
          Exemplaire posé sur l&apos;étagère des réservations
        </label>
        <input
          ref={inputRef}
          id={`${id}-barcode`}
          value={barcode}
          onChange={(e) => {
            setBarcode(e.target.value);
          }}
          placeholder="Scannez le code-barres"
          autoComplete="off"
          spellCheck={false}
          aria-describedby={shelve.error ? `${id}-error` : undefined}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        />
      </div>
      <Button type="submit" disabled={shelve.isPending || !barcode.trim()}>
        {shelve.isPending && <Loader2 className="animate-spin" aria-hidden />}
        Mettre à disposition
      </Button>
      {shelve.error && (
        <p
          id={`${id}-error`}
          role="alert"
          className="w-full text-sm text-red-700 dark:text-red-400"
        >
          {shelve.error.message}
        </p>
      )}
    </form>
  );
}

function ResultNotice({ result }: { result: ReadyForPickupResult }) {
  return (
    <p
      role="status"
      className={cn(
        "flex items-start gap-2 rounded-lg p-3 text-sm",
        result.patronNotified
          ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300"
          : "bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-200"
      )}
    >
      {result.patronNotified ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      ) : (
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      )}
      <span>
        <strong>{result.reservation.record.title}</strong> pour {result.reservation.patron.name} (
        {result.reservation.patron.barcode}). {result.message}
      </span>
    </p>
  );
}
